import React, { useEffect, useState } from "react";
import styles from "./conditional-dropdown-field.module.scss";
import SelectDropdownField, { DropdownOption } from "../../select-dropdown/SelectDropdownField";

export interface ConditionalParentOption {
	value: string;
	label: string;
	children: DropdownOption[];
}

interface ConditionalDropdownFieldProps {
	parentOptions: ConditionalParentOption[];
	childLabel?: string;
	selectedChildValue?: string;
	onChildSelected: (child: DropdownOption | null) => void;
	parentPlaceholder?: string;
	childPlaceholder?: string;
	hasError?: boolean;
}

const ConditionalDropdownField: React.FC<ConditionalDropdownFieldProps> = ({
	parentOptions,
	childLabel,
	selectedChildValue,
	onChildSelected,
	parentPlaceholder = "Select...",
	childPlaceholder = "Select...",
	hasError = false,
}) => {
	// Derive the parent that contains the preselected child (on mount / when preselection changes).
	const parentContainingChild = (childValue?: string) =>
		childValue ? parentOptions.find(p => p.children.some(c => c.value === childValue))?.value : undefined;

	const [selectedParentValue, setSelectedParentValue] = useState<string | undefined>(
		parentContainingChild(selectedChildValue)
	);

	// Adopt the parent of an externally preselected child. Skip when there is no child,
	// so clearing the child (e.g. right after the user switches parents) doesn't wipe the
	// just-selected parent and hide the child dropdown.
	useEffect(() => {
		if (!selectedChildValue) return;
		const derivedParent = parentContainingChild(selectedChildValue);
		if (derivedParent) setSelectedParentValue(derivedParent);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedChildValue]);

	const activeParent = parentOptions.find(p => p.value === selectedParentValue);

	const parentDropdownOptions: DropdownOption[] = parentOptions.map(p => ({ value: p.value, label: p.label }));

	return (
		<div className={styles.container}>
			<SelectDropdownField
				options={parentDropdownOptions}
				placeholder={parentPlaceholder}
				selectedValue={selectedParentValue}
				hasError={hasError}
				onSelectSingle={option => {
					if (option.value === selectedParentValue) return;
					setSelectedParentValue(option.value);
					onChildSelected(null); // reset child when parent changes
				}}
			/>

			{activeParent && (
				<div className={styles.childSection}>
					<span className={styles.childLabel}>{activeParent.label}</span>
					<SelectDropdownField
						options={activeParent.children}
						placeholder={childPlaceholder}
						selectedValue={selectedChildValue}
						hasError={hasError}
						onSelectSingle={option => onChildSelected(option)}
					/>
				</div>
			)}
		</div>
	);
};

export default ConditionalDropdownField;
